import { Dispatch } from '@reduxjs/toolkit';
import { History } from 'history';
import {
  Operation,
  OperationState,
  OperationType,
  RegistrationType,
} from '../models';
import { SignInResponse, SignUpResponse } from '../models/auth.models';
import { AppDispatch } from '../redux/core';
import { setOperation } from '../redux/general/general.actions';
import { setAccessToken, setRefreshToken } from '../redux/user/user.actions';
import { Routes } from '../routes/types';
import { parseJwt } from './jwt';
import { removeFromLocalStorage, StorageKey } from './localstorage';

export const getIsEmailConfirmed = (accessToken: string) => {
  const jwtData = parseJwt(accessToken);
  return !jwtData.nec;
};

export const logout = (dispatch: Dispatch) => {
  removeFromLocalStorage(StorageKey.AccessToken);
  removeFromLocalStorage(StorageKey.RefreshToken);
  dispatch(setAccessToken(null));
  dispatch(setRefreshToken(null));
};

export const handleSignIn = (
  successRes: { data: SignInResponse | SignUpResponse },
  operation: Operation | null,
  history: History
) => {
  if (successRes.data) {
    const isEmailConfirmed = getIsEmailConfirmed(
      successRes.data.tokens.accessToken
    );
    if (isEmailConfirmed) {
      followOperationIfNecessary(successRes, operation, history);
    } else {
      history.push(Routes.ConfirmEmail);
    }
  }
};

export const followOperationIfNecessary = (
  successRes: { data: SignInResponse | SignUpResponse },
  operation: Operation | null,
  history: History
) => {
  if (successRes.data && operation) {
    if (operation.registrationType === RegistrationType.Light) {
      history.push(operation.routing.url, operation.routing.state);
    } else {
      history.push(Routes.CompleteUserInfo);
    }
  } else if (successRes.data) {
    history.push(Routes.Home);
  }
};

export function setupNewAddressLightSignInOperation(dispatch: AppDispatch) {
  dispatch(
    setOperation({
      operationType: OperationType.AddressChange,
      operationState: OperationState.NotCompleted,
      routing: {
        url: Routes.CreateAddress,
        state: { isFromChangeAddress: true },
      },
      registrationType: RegistrationType.Light,
      savedData: {},
    })
  );
}

export function setupPickUpPointStrongSignInOperation(dispatch: AppDispatch) {
  dispatch(
    setOperation({
      operationType: OperationType.Pdr,
      operationState: OperationState.NotCompleted,
      routing: {
        url: Routes.SelectPickUpPoint,
        state: null,
      },
      registrationType: RegistrationType.Strong,
      savedData: {},
    })
  );
}

export function setupChooseDeliveryTimeStrongSignInOperation(
  dispatch: AppDispatch
) {
  dispatch(
    setOperation({
      operationType: OperationType.Reschedule,
      operationState: OperationState.NotCompleted,
      routing: {
        url: Routes.ChooseDeliveryTime,
        state: null,
      },
      registrationType: RegistrationType.Strong,
      savedData: {},
    })
  );
}

export function setupChooseDeliveryNoneOperation(dispatch: AppDispatch) {
  dispatch(
    setOperation({
      operationType: OperationType.FreeReschedule,
      operationState: OperationState.NotCompleted,
      routing: {
        url: Routes.ChooseDeliveryTime,
        state: null,
      },
      registrationType: RegistrationType.None,
      savedData: {},
    })
  );
}

export function setupOpeningHoursLightSignInOperation(dispatch: AppDispatch) {
  dispatch(
    setOperation({
      operationType: OperationType.OpeningHours,
      operationState: OperationState.NotCompleted,
      routing: {
        url: Routes.OpeningHours,
        state: null,
      },
      registrationType: RegistrationType.Light,
      savedData: {},
    })
  );
}

export function setupAddNotesLightSignInOperation(dispatch: AppDispatch) {
  dispatch(
    setOperation({
      operationType: OperationType.AddNotes,
      operationState: OperationState.NotCompleted,
      routing: {
        url: Routes.EditAddressNotes,
        state: null,
      },
      registrationType: RegistrationType.Light,
      savedData: {},
    })
  );
}
